import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Zap } from 'lucide-react'

import type { Customer } from '@/lib/api/customer-api'
import type { ProvinceWardValue } from '@/components/forms/province-ward-picker'
import type { OrdersSearch } from './index'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CustomerSearchSelect } from '@/components/forms/customer-search-select'
import { ProvinceWardPicker } from '@/components/forms/province-ward-picker'
import { useCreateOrder } from '@/hooks/use-sales-order-query'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'
import { DEFAULT_SHIPPING_CARRIER_ID, WALKIN_CUSTOMER } from '@/lib/constants'

export const Route = createFileRoute('/_app/_authed/_sidebar/orders/quick')({
    component: QuickOrderPage,
})

function QuickOrderPage() {
    const navigate = useNavigate()
    const createOrder = useCreateOrder()

    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
        null,
    )
    const [customerName, setCustomerName] = useState('')
    const [customerPhone, setCustomerPhone] = useState('')
    const [shippingAddress, setShippingAddress] = useState('')
    const [shipping, setShipping] = useState<ProvinceWardValue>({})
    const [productNotes, setProductNotes] = useState('')

    const handleCustomerChange = (customer: Customer | null) => {
        setSelectedCustomer(customer)
        if (customer) {
            setCustomerName(customer.name)
            setCustomerPhone(customer.phone ?? '')
            setShippingAddress(customer.address ?? '')
            setShipping({
                provinceCode: customer.province_code ?? undefined,
                provinceName: customer.province ?? undefined,
                wardCode: customer.ward_code ?? undefined,
                wardName: customer.ward ?? undefined,
            })
        } else {
            setCustomerName('')
            setCustomerPhone('')
            setShippingAddress('')
            setShipping({})
        }
    }

    // Walk-in fallback: backend requires customer_id, customer_name.
    // Other fields (phone/address) come from the form for delivery accuracy.
    const canSubmit =
        customerName.trim().length >= 2 &&
        customerPhone.trim().length >= 8 &&
        shippingAddress.trim().length >= 5 &&
        productNotes.trim().length >= 3 &&
        !createOrder.isPending

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return

        const buyer = selectedCustomer ?? WALKIN_CUSTOMER

        try {
            await createOrder.mutateAsync({
                source_channel: 'quick_order',
                customer_id: buyer.id,
                customer_name: customerName.trim() || buyer.name,
                customer_phone: customerPhone.trim(),
                shipping_address: shippingAddress.trim(),
                shipping_province: shipping.provinceName || undefined,
                shipping_province_code: shipping.provinceCode || undefined,
                shipping_ward: shipping.wardName || undefined,
                shipping_ward_code: shipping.wardCode || undefined,
                shipping_carrier_id: DEFAULT_SHIPPING_CARRIER_ID,
                product_notes: productNotes.trim(),
                items: [],
                // Đơn nhanh = nháp: tạo ở trạng thái draft, không auto-confirm.
                save_as_draft: true,
            })
            void navigate({ to: '/orders' })
        } catch {
            // error handled by mutation hook toast
        }
    }

    return (
        <div className="mx-auto max-w-lg space-y-4 page-pad pb-20 sm:pb-4">
            <div className="flex items-center gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <Zap className="size-5 text-primary" />
                <h1 className="text-xl font-semibold">
                    {m.order_quick_title()}
                </h1>
            </div>

            <form
                id="quick-order-form"
                onSubmit={handleSubmit}
                className="space-y-5 rounded-md border p-4 sm:p-6"
            >
                {/* Customer search */}
                <div className="space-y-1.5">
                    <Label>{m.customer_search()}</Label>
                    <CustomerSearchSelect
                        value={selectedCustomer}
                        onChange={handleCustomerChange}
                    />
                    <p className="text-xs text-muted-foreground">
                        {m.customer_search_walk_in_hint({
                            walkIn: m.customer_walk_in(),
                        })}
                    </p>
                </div>

                {/* Customer info — single column on mobile for easier input */}
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="qo-name">
                            {m.customer_name_label()} *
                        </Label>
                        <Input
                            id="qo-name"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            placeholder={m.customer_name_label()}
                            required
                            minLength={2}
                            autoComplete="name"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="qo-phone">
                            {m.customer_phone_label()} *
                        </Label>
                        <Input
                            id="qo-phone"
                            type="tel"
                            inputMode="tel"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(e.target.value)}
                            placeholder="0912345678"
                            required
                            minLength={8}
                            autoComplete="tel"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="qo-address">
                            {m.customer_shipping_address()} *
                        </Label>
                        <Input
                            id="qo-address"
                            value={shippingAddress}
                            onChange={(e) => setShippingAddress(e.target.value)}
                            placeholder={m.customer_shipping_address()}
                            required
                            minLength={5}
                            autoComplete="street-address"
                        />
                    </div>
                    <ProvinceWardPicker
                        value={shipping}
                        onChange={setShipping}
                    />
                </div>

                {/* Product notes - free text */}
                <div className="space-y-1.5">
                    <Label htmlFor="qo-products">{m.order_products()} *</Label>
                    <textarea
                        id="qo-products"
                        value={productNotes}
                        onChange={(e) => setProductNotes(e.target.value)}
                        placeholder={m.order_quick_products_placeholder()}
                        required
                        minLength={3}
                        rows={5}
                        className="border-input focus-visible:border-ring w-full rounded-md border bg-transparent px-3 py-2.5 text-base shadow-xs transition-[color,box-shadow] placeholder:text-muted-foreground min-w-0 outline-none resize-y disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <p className="text-xs text-muted-foreground">
                        {m.order_quick_product_hint()}
                    </p>
                </div>

                {/* Desktop buttons */}
                <div className="hidden gap-2 pt-2 sm:flex sm:justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                            void navigate({
                                to: '/orders',
                                search:
                                    recallListSearch<OrdersSearch>(
                                        LIST_SEARCH_KEYS.orders,
                                    ) ?? {},
                            })
                        }
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button type="submit" disabled={!canSubmit}>
                        {createOrder.isPending
                            ? m.common_creating()
                            : m.order_quick_title()}
                    </Button>
                </div>
            </form>

            {/* Mobile sticky bottom bar — form attribute links to the form */}
            <div className="fixed inset-x-0 bottom-0 border-t bg-background p-3 sm:hidden">
                <Button
                    type="submit"
                    form="quick-order-form"
                    className="w-full h-12 text-base"
                    disabled={!canSubmit}
                >
                    {createOrder.isPending
                        ? m.common_creating()
                        : m.order_quick_title()}
                </Button>
            </div>
        </div>
    )
}
